import { z } from 'zod';

export const crmTargetTypes = ['LEAD', 'CONTACT', 'COMPANY', 'DEAL'] as const;
export const crmTargetSchema = z.object({ targetType: z.enum(crmTargetTypes), targetId: z.string().min(1) });
export type CrmTarget = z.infer<typeof crmTargetSchema>;
export const activityTypes = ['CALL', 'EMAIL', 'MEETING', 'NOTE', 'TASK'] as const;
export const taskPriorities = ['LOW', 'NORMAL', 'HIGH'] as const;
export const taskStatuses = ['OPEN', 'COMPLETED'] as const;
const instant = z.iso.datetime({ offset: true });

export const createActivitySchema = crmTargetSchema.extend({
  type: z.enum(['CALL', 'EMAIL', 'MEETING', 'NOTE']),
  summary: z.string().trim().min(1).max(200),
  body: z.string().trim().max(10000).nullable().optional(),
  occurredAt: instant.optional(),
});
export type CreateActivity = z.infer<typeof createActivitySchema>;
export const activityQuerySchema = crmTargetSchema.extend({ cursor: z.string().optional(), limit: z.coerce.number().int().min(1).max(100).default(25) });
export type ActivityQuery = z.infer<typeof activityQuerySchema>;
export const activitySchema = crmTargetSchema.extend({
  id: z.string(), type: z.enum(activityTypes), summary: z.string(), body: z.string().nullable(),
  actorId: z.string(), actorName: z.string(), occurredAt: z.string(), createdAt: z.string(),
});
export type ActivityItem = z.infer<typeof activitySchema>;
export const activityListSchema = z.object({ items: z.array(activitySchema), nextCursor: z.string().nullable() });
export type ActivityList = z.infer<typeof activityListSchema>;

export const createTaskSchema = crmTargetSchema.extend({
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(10000).nullable().optional(),
  ownerId: z.string().min(1),
  dueAt: instant,
  timezone: z.string().min(1).max(80),
  priority: z.enum(taskPriorities).default('NORMAL'),
});
export type CreateTask = z.infer<typeof createTaskSchema>;
export const updateTaskSchema = z.object({
  expectedVersion: z.number().int().min(0),
  title: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(10000).nullable().optional(),
  ownerId: z.string().min(1).optional(),
  dueAt: instant.optional(),
  timezone: z.string().min(1).max(80).optional(),
  priority: z.enum(taskPriorities).optional(),
  status: z.enum(taskStatuses).optional(),
}).refine((value) => Object.keys(value).some((key) => key !== 'expectedVersion'), 'Change at least one field');
export type UpdateTask = z.infer<typeof updateTaskSchema>;
export const taskQuerySchema = z.object({
  view: z.enum(['all', 'today', 'overdue', 'upcoming', 'mine']).default('all'),
  targetType: z.enum(crmTargetTypes).optional(), targetId: z.string().optional(),
  dayStart: instant.optional(), dayEnd: instant.optional(),
  cursor: z.string().optional(), limit: z.coerce.number().int().min(1).max(100).default(25),
}).refine((value) => (value.targetType === undefined) === (value.targetId === undefined), 'Target type and ID must be supplied together')
  .refine((value) => !['today', 'upcoming'].includes(value.view) || (!!value.dayStart && !!value.dayEnd && Date.parse(value.dayStart) < Date.parse(value.dayEnd)), 'Supply the local day boundaries');
export type TaskQuery = z.infer<typeof taskQuerySchema>;
export const taskSchema = crmTargetSchema.extend({
  id: z.string(), title: z.string(), description: z.string().nullable(),
  ownerId: z.string(), ownerName: z.string(), creatorId: z.string(),
  dueAt: z.string(), timezone: z.string(), priority: z.enum(taskPriorities), status: z.enum(taskStatuses),
  completedAt: z.string().nullable(), version: z.number().int(), createdAt: z.string(), updatedAt: z.string(),
  targetLabel: z.string(),
});
export type TaskItem = z.infer<typeof taskSchema>;
export const taskListSchema = z.object({ items: z.array(taskSchema), nextCursor: z.string().nullable(), filteredCount: z.number().int() });
export type TaskList = z.infer<typeof taskListSchema>;
